import { useState, useEffect } from 'react'
import { Evidence } from '../types/assessment'

const GLOBAL_EVIDENCE_KEY_PREFIX = 'provenance_one_global_evidence_'

export interface GlobalEvidence extends Evidence {
  isGlobal: true
  name: string // Custom display name for the evidence
  tags?: string[]
  description?: string
  nextReviewDate?: string // ISO date string for when evidence should be reviewed
}

export function useGlobalEvidence(userEmail: string | null) {
  const [globalEvidence, setGlobalEvidence] = useState<GlobalEvidence[]>([])

  const getStorageKey = () => {
    return userEmail ? `${GLOBAL_EVIDENCE_KEY_PREFIX}${userEmail}` : null
  }

  // Load global evidence from localStorage
  useEffect(() => {
    const key = getStorageKey()
    if (!key) {
      setGlobalEvidence([])
      return
    }

    const stored = localStorage.getItem(key)
    if (stored) {
      try {
        setGlobalEvidence(JSON.parse(stored))
      } catch (e) {
        console.error('Failed to load global evidence:', e)
        setGlobalEvidence([])
      }
    } else {
      setGlobalEvidence([])
    }
  }, [userEmail])

  // Save global evidence to localStorage
  const saveGlobalEvidence = (evidence: GlobalEvidence[]) => {
    const key = getStorageKey()
    if (!key) return

    localStorage.setItem(key, JSON.stringify(evidence))
    setGlobalEvidence(evidence)
  }

  const addGlobalEvidence = (evidence: Omit<GlobalEvidence, 'isGlobal'>) => {
    const newEvidence: GlobalEvidence = {
      ...evidence,
      isGlobal: true,
    }
    const updated = [...globalEvidence, newEvidence]
    saveGlobalEvidence(updated)
  }

  const updateGlobalEvidence = (
    evidenceId: string,
    updates: Partial<GlobalEvidence>,
  ) => {
    const updated = globalEvidence.map((e) =>
      e.id === evidenceId ? { ...e, ...updates } : e,
    )
    saveGlobalEvidence(updated)
  }

  const deleteGlobalEvidence = (evidenceId: string) => {
    const updated = globalEvidence.filter((e) => e.id !== evidenceId)
    saveGlobalEvidence(updated)
  }

  const getGlobalEvidence = (
    evidenceId: string,
  ): GlobalEvidence | undefined => {
    return globalEvidence.find((e) => e.id === evidenceId)
  }

  // Helper to check if evidence is overdue for review
  const isOverdueForReview = (evidence: GlobalEvidence): boolean => {
    if (!evidence.nextReviewDate) return false
    return new Date(evidence.nextReviewDate) < new Date()
  }

  return {
    globalEvidence,
    addGlobalEvidence,
    updateGlobalEvidence,
    deleteGlobalEvidence,
    getGlobalEvidence,
    isOverdueForReview,
  }
}
